import {resolveBackground} from '#core/utils/color-utils.js';
import {buildIntentsFromConfig} from '#core/utils/intent-factory.js';
import {icon} from '#core/utils/style-utils.js';
import {merge} from '#core/utils/config-utils.js';
import {isMobile} from '#core/utils/context-utils.js';

import {ApplicationConfig} from '#core/config/application-config.js';
import {Intents} from '#core/application/intents/index.js';

import {generatePivotButton} from '#core/application/modules/tiles/pivot/index.js';
import {generatePrimaryButton, generateSecondaryButton} from '#core/application/modules/tiles/button/index.js';
import {generatePopover, generateSimplePopover} from '#core/application/modules/tiles/popover/index.js';
import {generateSettingMenuGlobalStuff} from '#core/application/modules/tiles/settings/index.js';
import {generateSkeletonPart} from '#core/application/modules/tiles/skeleton-part/index.js';
import {generateRecentSearch, generateSearchResultItem} from '#core/application/modules/tiles/search/search-result.js';
import {generateSearchInput} from '#core/application/modules/tiles/search/search-input.js';

export function applicationHeaderFactory(PALETTE) {
    const {FONT} = PALETTE;

    const crossSellMenuLinks = buildIntentsFromConfig(ApplicationConfig.config.crossSellMenuLinks);
    const HEADER_SHADOW = {
        ...(PALETTE.APPLICATION_HEADER_MOBILE_SHOW_SHADOW
            ? {_mobile_boxShadow: PALETTE.APPLICATION_HEADER_SHADOW}
            : {}),
        ...(PALETTE.APPLICATION_HEADER_DESKTOP_SHOW_SHADOW
            ? {_desktop_boxShadow: PALETTE.APPLICATION_HEADER_SHADOW}
            : {}),
    };
    return {
        ApplicationHeader: {
            headerContentContainer: {
                ...resolveBackground(PALETTE.APPLICATION_HEADER_BG),
                // ...HEADER_SHADOW, //TODO: Header shadow doesn't allowed in mojito :(
            },
            normalLayout: {
                applicationMenu: {
                    style: {
                        sideColumns: {
                            flexBasis: '30%',
                        },
                    },
                },
                mainNavigationBar: {
                    rightContainer: {
                        style: {
                            alignItems: 'center',
                        },
                    },
                    searchControllerView: {
                        searchView: {
                            searchResultsPopup: {
                                container: {
                                    style: {
                                        right: 0,
                                    },
                                },
                            },
                        },
                    },
                },
            },
            compactLayout: {
                mainNavigationBar: {
                    searchControllerView: {
                        searchView: {
                            searchResultsPopup: {
                                container: {
                                    style: {
                                        left: 16,
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
        ApplicationHeaderSkeleton: {
            container: {
                style: {
                    height: '104px',
                    ...resolveBackground(PALETTE.APPLICATION_HEADER_BG),
                    ...HEADER_SHADOW,
                },
            },
            navigationBarContainer: {
                style: {
                    height: '40px',
                    ...resolveBackground(PALETTE.APPLICATION_HEADER_NAV_BAR_BG),
                },
            },
            buttonSkeleton: generateSkeletonPart(PALETTE.APPLICATION_HEADER_BUTTONS_SKELETON),
        },
        CrossSellMenu: {
            // Please do not put here styles
            // Instead use ones in ApplicationMenu and AZOverview
            // Because they are completely different for desktop and mobile
        },
        ApplicationMenu: {
            style: {
                innerContainer: {
                    flexBasis: 64,
                    _mobile_flexBasis: 48,
                },
            },
            logo: {
                src: 'generic/logo.svg',
                style: {
                    flexGrow: 0,
                },
            },
            multiRowLayout: {
                crossSellMenu: buildDesktopCrossSellConfig(PALETTE, crossSellMenuLinks, false),
            },
            singleRowLayout: {
                crossSellMenu: buildDesktopCrossSellConfig(PALETTE, crossSellMenuLinks, true),
            },
            searchIcon: icon(PALETTE.APPLICATION_HEADER_SEARCH_BUTTON.TERTIARY_BUTTON_ICON_COLOR),
            systemInitialisation: {
                timeRemaining: {
                    style: {
                        fontFamily: FONT.FAMILY,
                        fontSize: FONT.SIZE_MEDIUM,
                        fontWeight: FONT.WEIGHT_NORMAL,
                        ...PALETTE.APPLICATION_HEADER_SYSTEM_INITIALIZATION_TEXT,
                    },
                },
                title: {
                    style: {
                        fontFamily: FONT.FAMILY,
                        fontSize: 24,
                        fontWeight: FONT.WEIGHT_NORMAL,
                        ...PALETTE.APPLICATION_HEADER_SYSTEM_INITIALIZATION_TEXT,
                    },
                },
            },
        },
        UserAuthenticationInfo: {
            loginButton: {
                button: merge(generateSecondaryButton(PALETTE.APPLICATION_HEADER_LOGIN_BUTTON), {
                    style: {
                        base: {
                            padding: '0 8px',
                        },
                    },
                }),
            },
            signupButton: {
                button: merge(generatePrimaryButton(PALETTE.APPLICATION_HEADER_SIGNUP_BUTTON), {
                    style: {
                        base: {
                            padding: '0 8px',
                        },
                    },
                }),
            },
        },
        AccountMenu: {
            popupToggleButton: {
                style: {
                    base: {
                        flexGrow: 0,
                    },
                },
            },
            popupToggleButtonInfoPane: {
                itemSpacing: 2,
                style: {
                    padding: '0 8px 0 0',
                },
            },
            popupToggleButtonIcon: {
                src: 'account',
                style: {
                    fill: PALETTE.APPLICATION_HEADER_USER_INFO_ICON_COLOR,
                    minHeight: 24,
                    minWidth: 24,
                    alignSelf: 'center',
                },
            },
            logout: {
                hide: true,
            },
            usernameText: {
                style: {
                    fontSize: FONT.SIZE_XXSMALL,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    ...PALETTE.APPLICATION_HEADER_USER_INFO_NAME_TEXT,
                },
            },
            balanceValueText: {
                style: {
                    fontSize: FONT.SIZE_SMALL,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    ...PALETTE.APPLICATION_HEADER_USER_INFO_BALANCE_TEXT,
                },
            },
        },
        MainNavigationBar: {
            style: {
                rootContainer: {
                    ...resolveBackground(PALETTE.APPLICATION_HEADER_NAV_BAR_BG),
                    padding: '0 8px',
                },
            },
            linkButtons: {
                disableSelectedButton: !ApplicationConfig.isEmbedded,
                container: {
                    style: {
                        justifyContent: 'flex-start',
                    },
                },
                selectableButton: generatePivotButton(PALETTE.APPLICATION_HEADER_NAV_BAR_PAGE_SELECTOR),
            },
            helpButton: {
                style: {
                    base: {
                        flexDirection: 'column',
                        flexBasis: 40,
                    },
                },
            },
            helpIcon: {
                style: {
                    fill: PALETTE.APPLICATION_HEADER_NAV_BAR_HELP_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
                    width: 16,
                    height: 16,
                    flexGrow: 0,
                },
            },
            showHelpButton: !!Intents.HELP,
            // Settings icon config is located in SettingsMenu component
            linkTextContainer: {
                style: {
                    flexBasis: '48px',
                },
            },
            // override default values from "main-navigation-bar.yaml"
            searchControllerView: {
                searchView: {
                    searchInput: merge(generateSearchInput(PALETTE.APPLICATION_HEADER_NAV_BAR_SEARCH_INPUT), {
                        container: {
                            inactive: {
                                style: {
                                    maxWidth: 'initial',
                                },
                            },
                            active: {
                                style: {
                                    minWidth: 'initial',
                                },
                            },
                        },
                        style: {
                            wrapperContainer: {
                                margin: 0,
                            },
                        },
                    }),
                    messageText: {
                        style: {
                            fontSize: FONT.SIZE_MEDIUM,
                            fontWeight: FONT.WEIGHT_NORMAL,
                            wordBreak: 'break-word',
                            ...PALETTE.SEARCH_RESULT.SEARCH_RECENT_ITEM_TEXT,
                        },
                    },
                    searchResultsPopup: generatePopover(PALETTE.SEARCH_POPOVER),
                },
            },
            sessionTime: {
                container: {
                    style: {
                        padding: '0 8px',
                    },
                },
                timerVertical: {
                    startTimeLabel: {
                        container: {
                            padding: 0,
                        },
                        style: {
                            fontSize: FONT.SIZE_XSMALL,
                            fontWeight: FONT.WEIGHT_NORMAL,
                            ...PALETTE.APPLICATION_HEADER_NAV_BAR_SESSION_TIME_LABEL_TEXT,
                        },
                    },
                    timeString: {
                        style: {
                            fontSize: FONT.SIZE_SMALL,
                            fontWeight: FONT.WEIGHT_NORMAL,
                            ...PALETTE.APPLICATION_HEADER_NAV_BAR_SESSION_TIME_VALUE_TEXT,
                        },
                    },
                },
            },
            currentTime: {
                container: {
                    style: {
                        flexDirection: 'row',
                    },
                },
                time: {
                    timeBox: {
                        style: {
                            fontSize: 11,
                            ...PALETTE.APPLICATION_HEADER_NAV_BAR_CURRENT_TIME_TEXT,
                        },
                    },
                },
                offset: {
                    container: {
                        padding: '0 0 0 4px',
                    },
                    style: {
                        fontSize: 11,
                        ...PALETTE.APPLICATION_HEADER_NAV_BAR_CURRENT_TIME_OFFSET_TEXT,
                    },
                },
            },
        },
        ...getSettingsPopupConfig(PALETTE.APPLICATION_HEADER_NAV_BAR_SETTINGS),
        RecentSearch: !isMobile() ? generateRecentSearch(PALETTE.SEARCH_RESULT) : {},
        SearchResultItem: !isMobile() ? generateSearchResultItem(PALETTE.SEARCH_RESULT) : {},
    };
}

function getSettingsPopupConfig(PALETTE) {
    return {
        // It exists only in desktop and in header
        SettingsMenuPopup: {
            icon: {
                style: {
                    minWidth: '16px',
                    minHeight: '16px',
                    alignSelf: 'center',
                },
            },
            button: {
                style: {
                    base: {
                        padding: '12px 0px',
                        fontSize: '14px',
                    },
                },
            },
            iconColor: {
                closed: PALETTE.HEADER_SETTINGS_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
                open: PALETTE.HEADER_SETTINGS_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
            },
            popup: merge(
                generateSimplePopover(
                    PALETTE.HEADER_SETTINGS_POPOVER_BG,
                    PALETTE.HEADER_SETTINGS_POPOVER_BORDER_RADIUS,
                    PALETTE.HEADER_SETTINGS_POPOVER_SHADOW,
                    PALETTE.HEADER_SETTINGS_POPOVER_BORDER_COLOR
                ),
                {
                    arrowheadOffset: '-8px',
                    container: {
                        style: {
                            width: '272px',
                            top: '40px',
                            right: '6px',
                        },
                    },
                }
            ),
        },

        // Made it global as it is no possibility to pass it to SettingsMenuPopup
        // Anyway for desktop it used only once - in header
        ...(!isMobile() ? generateSettingMenuGlobalStuff(PALETTE.HEADER_SETTINGS) : {}),
    };
}
function buildDesktopCrossSellConfig(PALETTE, LINKS, IS_SINGLE_ROW = false) {
    const BASE_STYLES = IS_SINGLE_ROW
        ? {
              flex: '0 1 auto',
              padding: 0,
          }
        : {
              flex: '0 1 auto',
              padding: '0 26px',
          };

    return {
        links: LINKS,
        linkButtons: {
            container: {
                itemSpacing: 32,
                style: {
                    padding: 0,
                },
            },
            selectableButton: merge(generatePivotButton(PALETTE.APPLICATION_HEADER_CROSS_APP_SELECTOR), {
                selected: {
                    style: {
                        base: BASE_STYLES,
                    },
                },
                unselected: {
                    style: {
                        base: BASE_STYLES,
                    },
                },
                indicator: {
                    style: {
                        base: {
                            left: 0,
                            right: 0,
                        },
                    },
                },
            }),
            disableSelectedButton: false,
        },
        linkLabel: {
            style: {
                padding: 0,
            },
        },
    };
}
